import { forwardRef, memo, useCallback } from 'react'
import { useAttachmentStore, useCurrentChatInput, useIsMaxAttachmentsReached, useSetChatInput } from '../../../../store'
import { useResetChatContext } from '../../../../store/atoms'
import { useTrackAddAttachment } from '../../../../hooks/attachments-hooks'
import { shouldPasteAsAttachment } from '../../../../utils/fileUtils'
import { acceptedClipboardFiles, useAttachFiles } from '../hooks/use-attach-files'
import { SendMessageOptions } from '../../../../types'
import { MentionableChangeEvent, MentionableInput } from '../../../elements/MentionableInput'
import { useMentionsHandler } from '../mentions/hooks/use-mentions-handler'
import { ChatInputElementId } from '../chat-input-element-id'
import { useTypeToFocusChatInput } from '../hooks/use-type-to-focus-chat-input'
import { useSubmitComposer } from '../hooks/use-submit-composer'
import { usePrefetchSendChallenge } from '../../../../hooks/use-send-challenge'
import { InputPlaceholder } from './input-placeholder'

/** The composer textarea: mention-aware editing, submit-on-Enter, and paste-to-attach. */
export const MessageInput = memo(
  forwardRef<
    HTMLElement,
    {
      chatId?: string
      placeholder?: string
      disabled?: boolean
      onSubmit?: (conversationId: string) => void
      handleSubmit?: () => void
      prefetchesSendChallenge?: boolean
      isLoading?: boolean
      typeToFocusDisabled?: boolean
    }
  >((props, ref) => {
    const { chatId, placeholder, disabled, onSubmit, handleSubmit, isLoading, typeToFocusDisabled } = props
    const { prefetchesSendChallenge = true } = props
    const { onChangeHandler, onSelectionChangeHandler } = useMentionsHandler()
    const chatInput = useCurrentChatInput()
    const setChatInput = useSetChatInput()
    const resetContext = useResetChatContext()
    const { addClipboardAttachment } = useAttachmentStore()
    const isMaxAttachmentsReached = useIsMaxAttachmentsReached()
    const trackAddAttachment = useTrackAddAttachment()
    const attachFiles = useAttachFiles()
    const { isBlockedForKeyboard, dispatchSubmit } = useSubmitComposer({ chatId, onSubmit })
    const prefetchSendChallenge = usePrefetchSendChallenge()

    const onChange = useCallback(
      (e: MentionableChangeEvent) => {
        const value = e.target.value
        setChatInput(value)
        onChangeHandler(e)
        if (prefetchesSendChallenge && value.trim()) prefetchSendChallenge()
      },
      [onChangeHandler, prefetchesSendChallenge, prefetchSendChallenge],
    )

    const onKeyDown = async (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()

        if (handleSubmit) {
          if (!isLoading) {
            handleSubmit()
          }
          return
        }

        if (isBlockedForKeyboard) {
          return
        }

        // If using cmd + enter / ctrl + enter, submit without any attachments
        const isCtrlOrCmdPressed = e.metaKey || e.ctrlKey
        const overrides: Partial<SendMessageOptions> = isCtrlOrCmdPressed ? { attachments: [] } : {}
        dispatchSubmit(overrides)
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && e.shiftKey && (e.metaKey || e.ctrlKey)) {
        resetContext()
        window.ampltd.trackEvent('Reset Attachments Hotkey')
      }
    }

    const onPaste = useCallback(
      async (e: React.ClipboardEvent) => {
        // Read the clipboard synchronously — it becomes inaccessible once this
        // handler yields on its first await.
        const files = acceptedClipboardFiles(e.clipboardData)
        if (files.length > 0) {
          // Stop the document-level FilePasteHandler from also attaching the same files
          e.stopPropagation()
          await attachFiles(files)
          return true
        }

        // For clipboard pastes where it's text over 30 chars, attach it as a clipboard attachment
        const pastedText = e.clipboardData.getData('text')
        if (pastedText && shouldPasteAsAttachment(pastedText)) {
          if (isMaxAttachmentsReached) {
            return false
          }
          addClipboardAttachment({
            type: 'text',
            value: pastedText,
          })

          trackAddAttachment({
            type: 'clipboardText',
            trigger: 'Chat Input Paste',
          })
          return true
        }
        return false
      },
      [addClipboardAttachment, attachFiles, isMaxAttachmentsReached, trackAddAttachment],
    )

    useTypeToFocusChatInput(typeToFocusDisabled)

    return (
      <MentionableInput
        ref={ref}
        id={ChatInputElementId}
        autoFocus={false}
        placeholder={placeholder ?? <InputPlaceholder />}
        value={chatInput}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onSelect={onSelectionChangeHandler}
        onPaste={onPaste}
        disabled={disabled}
        // The row bottom-aligns this against the 32px control cluster, which left
        // a single line sitting below the buttons' centre. Taller content outgrows
        // the min-height and bottom-aligns as before.
        wrapperClassName={'flex min-h-8 w-fit flex-col justify-center font-sans text-md font-normal'}
        className="!selection:text-foreground"
      />
    )
  }),
)
MessageInput.displayName = 'MessageInput'
